import { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CVLockup } from './Logo';

const ABOUT_VIDEO = 'https://videos.pexels.com/video-files/32699825/13940581_2560_1440_30fps.mp4';
const ABOUT_POSTER = 'https://images.unsplash.com/photo-1770745560263-a8fc696de90b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjB0ZXh0dXJlfGVufDB8fHx8MTc3NTM0MjEwOXww&ixlib=rb-4.1.0&q=85';

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

export default function About() {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = true;
      videoRef.current.play().catch(() => {});
    }
  }, []);

  return (
    <section id="studio" className="py-24 md:py-36" data-testid="about-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="section-divider mb-12" />

        <motion.p className="section-number" {...fadeUp} data-testid="about-number">
          01 / The Studio
        </motion.p>
        <motion.h2
          className="section-title mt-3 mb-16 md:mb-20"
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.1 }}
          data-testid="about-title"
        >
          Creative Direction
          <br />
          Meets Intelligent
          <br />
          Execution.
        </motion.h2>

        <div className="manifesto-grid" data-testid="manifesto-grid">
          {/* Left: Video with logo overlay */}
          <motion.div
            className="manifesto-media"
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <video
              ref={videoRef}
              className="manifesto-video"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              poster={ABOUT_POSTER}
              data-testid="about-video"
            >
              <source src={ABOUT_VIDEO} type="video/mp4" />
            </video>
            <div className="manifesto-video-overlay" />
            <div className="manifesto-logo-overlay">
              <CVLockup />
            </div>
            <div className="manifesto-image-label">
              <span>Creative AI Venture Studio</span>
            </div>
          </motion.div>

          {/* Right: Studio description */}
          <div className="manifesto-text-col">
            <motion.p
              className="manifesto-lead"
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.2 }}
              data-testid="manifesto-lead"
            >
              We work with people who have an idea,
              a problem, or an ambition &mdash; and need a partner
              with taste, technical fluency, and strategic
              clarity to give it form.
            </motion.p>

            <motion.div
              className="manifesto-body"
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.3 }}
            >
              <p data-testid="manifesto-text-1">
                Civic Ventures is a creative AI venture studio. We combine
                creative direction, AI strategy, software thinking,
                and hands-on execution to help ambitious people move
                from &ldquo;what if&rdquo; to &ldquo;what&apos;s next.&rdquo;
              </p>
              <p data-testid="manifesto-text-2">
                Whether you&apos;re exploring a new concept, trying to
                understand what AI can do for your business, or ready
                to build something tangible &mdash; this is where it starts.
              </p>
            </motion.div>

            <motion.div
              className="manifesto-tagline"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.7, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              data-testid="manifesto-tagline"
            >
              Ideas. Strategy.
              <br />
              AI. Craft.
            </motion.div>

            {/* Brand conviction */}
            <motion.div
              className="mt-10 pt-8 border-t border-[var(--cv-border)]"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.8, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              data-testid="founder-section"
            >
              <p
                className="text-sm text-[var(--cv-text-dim)] leading-relaxed max-w-[400px]"
                style={{ fontFamily: 'var(--font-body)' }}
                data-testid="founder-bio"
              >
                Built from conviction that the best ideas deserve
                more than a slide deck &mdash; they deserve real-world form.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
